import { invalidate } from '@react-three/fiber';
import { useCallback, useEffect, useMemo } from 'react';
import { BufferAttribute, BufferGeometry, Color, DoubleSide, type InstancedMesh, Matrix4, MeshBasicMaterial } from 'three';

import type { DimensionPanel } from 'app/modules/wallforming/components/scenes/objects/dimensions/types/dimensionPanel';

const PANEL_FILL_OFFSET = 0.004;
const BBOX_FILL_OFFSET = 0.003;

const PANEL_OPACITY = 0.85;
const BBOX_OPACITY = 0.3;

const DEFAULT_GROUP_COLOR = '#4f46e5';
const NON_GROUP_COLOR = '#ffffff';

// Unit quad centered at origin (shared across all instances)
const UNIT_QUAD_POSITIONS = new Float32Array([
    -0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, 0.5, 0
]);

interface PanelDimensionPlaneProps {
    panels: DimensionPanel[];
    groupColors: Record<string, string>;
}

export const PanelDimensionPlane = ({ panels, groupColors }: PanelDimensionPlaneProps) => {
    const geometry = useMemo(() => {
        const g = new BufferGeometry();
        g.setAttribute('position', new BufferAttribute(UNIT_QUAD_POSITIONS.slice(), 3));
        g.computeBoundingSphere();
        return g;
    }, []);

    const panelMaterial = useMemo(
        () => new MeshBasicMaterial({ transparent: true, opacity: PANEL_OPACITY, depthTest: true, side: DoubleSide }),
        []
    );

    const boundingBoxMaterial = useMemo(
        () => new MeshBasicMaterial({ transparent: true, opacity: BBOX_OPACITY, depthTest: true, side: DoubleSide }),
        []
    );

    // Map between group and panels for bounding box computation
    const groupedPanels = useMemo(() => {
        const map = new Map<string, DimensionPanel[]>();
        for (const p of panels) {
            if (p.groupId != null) {
                let arr = map.get(p.groupId);
                if (!arr) {
                    arr = [];
                    map.set(p.groupId, arr);
                }
                arr.push(p);
            }
        }
        return map;
    }, [panels]);

    // Precompute bbox matrices and colors for each group (one entry per group)
    const groupBoundingBoxes = useMemo(() => {
        const result: { matrix: Matrix4; color: string }[] = [];
        for (const [groupId, gPanels] of groupedPanels) {
            const { u, v, normal } = gPanels[0];
            let minU = Infinity,
                maxU = -Infinity;
            let minV = Infinity,
                maxV = -Infinity;

            for (const panel of gPanels) {
                const uc = u.x * panel.center.x + u.y * panel.center.y + u.z * panel.center.z;
                const vc = v.x * panel.center.x + v.y * panel.center.y + v.z * panel.center.z;
                minU = Math.min(minU, uc - panel.width / 2);
                maxU = Math.max(maxU, uc + panel.width / 2);
                minV = Math.min(minV, vc - panel.height / 2);
                maxV = Math.max(maxV, vc + panel.height / 2);
            }

            const centerU = (minU + maxU) / 2;
            const centerV = (minV + maxV) / 2;
            const width = maxU - minU;
            const height = maxV - minV;
            const px = u.x * centerU + v.x * centerV + normal.x * BBOX_FILL_OFFSET;
            const py = u.y * centerU + v.y * centerV + normal.y * BBOX_FILL_OFFSET;
            const pz = u.z * centerU + v.z * centerV + normal.z * BBOX_FILL_OFFSET;

            result.push({
                matrix: new Matrix4().set(
                    u.x * width,
                    v.x * height,
                    normal.x,
                    px,
                    u.y * width,
                    v.y * height,
                    normal.y,
                    py,
                    u.z * width,
                    v.z * height,
                    normal.z,
                    pz,
                    0,
                    0,
                    0,
                    1
                ),
                color: groupColors[groupId] ?? DEFAULT_GROUP_COLOR
            });
        }
        return result;
    }, [groupedPanels, groupColors]);

    const m4 = useMemo(() => new Matrix4(), []);
    const tmpColor = useMemo(() => new Color(), []);

    const handleOnPanelPlaneRef = useCallback(
        (instance: InstancedMesh | null) => {
            if (!instance) {
                return;
            }
            panels.forEach((panel, i) => {
                const { u, v, normal, center, width, height, groupId } = panel;
                const px = center.x + normal.x * PANEL_FILL_OFFSET;
                const py = center.y + normal.y * PANEL_FILL_OFFSET;
                const pz = center.z + normal.z * PANEL_FILL_OFFSET;
                m4.set(
                    u.x * width,
                    v.x * height,
                    normal.x,
                    px,
                    u.y * width,
                    v.y * height,
                    normal.y,
                    py,
                    u.z * width,
                    v.z * height,
                    normal.z,
                    pz,
                    0,
                    0,
                    0,
                    1
                );
                instance.setMatrixAt(i, m4);
                const hex = groupId != null ? (groupColors[groupId] ?? DEFAULT_GROUP_COLOR) : NON_GROUP_COLOR;
                instance.setColorAt(i, tmpColor.set(hex));
            });
            instance.instanceMatrix.needsUpdate = true;
            if (instance.instanceColor) {
                instance.instanceColor.needsUpdate = true;
            }
            invalidate();
        },
        [groupColors, m4, panels, tmpColor]
    );

    const handleOnBoundingBoxPlaneRef = useCallback(
        (instance: InstancedMesh | null) => {
            if (!instance) {
                return;
            }
            groupBoundingBoxes.forEach(({ matrix, color }, i) => {
                instance.setMatrixAt(i, matrix);
                instance.setColorAt(i, tmpColor.set(color));
            });
            instance.instanceMatrix.needsUpdate = true;
            if (instance.instanceColor) {
                instance.instanceColor.needsUpdate = true;
            }
            invalidate();
        },
        [groupBoundingBoxes, tmpColor]
    );

    useEffect(
        () => () => {
            geometry.dispose();
            panelMaterial.dispose();
            boundingBoxMaterial.dispose();
        },
        [geometry, panelMaterial, boundingBoxMaterial]
    );

    if (panels.length === 0) {
        return null;
    }

    return (
        <>
            <instancedMesh
                ref={handleOnPanelPlaneRef}
                args={[geometry, panelMaterial, panels.length]}
                frustumCulled={false}
                renderOrder={1}
            />

            {groupBoundingBoxes.length > 0 && (
                <instancedMesh
                    ref={handleOnBoundingBoxPlaneRef}
                    args={[geometry, boundingBoxMaterial, groupBoundingBoxes.length]}
                    frustumCulled={false}
                    renderOrder={0}
                />
            )}
        </>
    );
};
